import { useIsMobile } from "@/hooks/use-mobile";
import { formatAmount, getRequiredFields, parseRelatedDocuments, toApiSort } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetGlMasterData,
  useGetPaymentMethodByCommunity,
  useGetParkedList,
  useGetPostedList,
} from "@/services";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  parkDocumentSearch,
  postDocumentSearch,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "./consts";
import { Button } from "@/components/ui";
import { setInvoiceFrom, setInvoiceIds, setPaymentIds } from "@/redux/slices";

const usePaymentListing = () => {
  const { paymentAction, glId, month } = useParams();
  const isPosted = paymentAction == "post";
  const isFromPayment360 = !!glId;
  const fromDashboard = useLocation().pathname?.includes("/dashboard");
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const [showLinkPopup, setShowLinkPopup] = useState<boolean>(false);
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const invoiceIds = useSelector(
    (state: RootState) => state.currentData.invoiceIds
  );

  const paymentIds = useSelector(
    (state: RootState) => state.currentData.paymentIds
  );
  const isViewPaymentDoc = useRef<boolean>(false);
  
  const { data: paymentMethodOptions, isLoading: isPaymentMethodLoading } =
    useGetPaymentMethodByCommunity({communityId: communityDetail?.communityId});

  const { data: glData, isLoading: isLoadingGlMaster } = useGetGlMasterData({
    communityId: communityDetail?.communityId,
    queryParams: {
      enabled: !!communityDetail?.communityId,
    },
  });

  const {
    data: parkDocumentList,
    isLoading: isParkDocLoading,
    refetch: refetchParkDoc,
    isRefetching: isParkDocRefetching,
  } = useGetParkedList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "year",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "documentType",
          value: "VP",
          operator: "EQUAL",
        },
        {
          field: "status",
          value: "POSTED",
          operator: "NOT_EQUAL",
        },
        {
          field: "sourceSystem",
          value: "MANUAL_JE",
          operator: "NOT_EQUAL",
        },
        ...(isViewPaymentDoc.current && paymentIds?.length
          ? [
              {
                field: "postDocumentHeaderId",
                value: paymentIds,
                operator: "CONTAINS_ANY",
              },
            ]
          : []),
        ...(glId && month
          ? [
              // {
              //   field: "glMasterId",
              //   value: Number(glId),
              //   operator: "EQUAL",
              // },
              // {
              //   field: "budgetMonth",
              //   value: month,
              //   operator: "EQUAL",
              // },
            ]
          : []),
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, parkDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
        // ...(paymentAction != "all"
        //   ? [
        //       {
        //         field: "status",
        //         value: paymentAction?.toUpperCase(),
        //         operator: "EQUAL",
        //       },
        //     ]
        //   : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: !isPosted,
    },
  });

  const {
    data: postDocumentList,
    isLoading: isPostDocLoading,
    refetch: refetchPostDoc,
    isRefetching: isPostDocRefetching,
  } = useGetPostedList({
    communityId: communityDetail?.communityId,
    data: {
      filters: [
        {
          field: "year",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "documentType",
          value: "VP",
          operator: "EQUAL",
        },
        {
          field: "status",
          value: "POSTED",
          operator: "EQUAL",
        },
        ...(glId && month
          ? [
              // {
              //   field: "glMasterId",
              //   value: Number(glId),
              //   operator: "EQUAL",
              // },
              // {
              //   field: "budgetMonth",
              //   value: month,
              //   operator: "EQUAL",
              // },
            ]
          : []),
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, postDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
        // ...(paymentAction != "all"
        //   ? [
        //       {
        //         field: "status",
        //         value: paymentAction?.toUpperCase(),
        //         operator: "EQUAL",
        //       },
        //     ]
        //   : []),
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
    queryParams: {
      enabled: isPosted,
    },
  });

  const documentList = isPosted ? postDocumentList : parkDocumentList ;
  const isDocLoading = isPosted ? isPostDocLoading : isParkDocLoading ;
  const refetchDoc = isPosted ? refetchPostDoc : refetchParkDoc;
  const isDocRefetching = isPosted ? isPostDocRefetching : isParkDocRefetching ;

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    isViewPaymentDoc.current = false;
    setTimeout(() => refetchDoc(), 100);
  };

  const breadcrumbDataPayment = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: "",
        name: <span className="text-primary">Payment</span>,
      },
    ],
    []
  );

  const breadcrumbDataPayment360 = useMemo(
    () =>
      fromDashboard
        ? [
            {
              link: `/${routes.cam}/${routes.dashboard}`,
              name: `Dashboard - ${communityDetail?.name || ""}`,
            },
            {
              link: `/${routes.cam}/${routes.dashboard}/${routes.payment}/${routes.reports}`,
              name: <span>Payment 360 Report</span>,
            },
            {
              link: "",
              name: <span className="text-primary">Invoice List</span>,
            },
          ]
        : [
            {
              link: `/${routes.cam}/${routes.activityDashboard}`,
              name: `Activity Dashboard - ${communityDetail?.name || ""}`,
            },
            {
              link: `/${routes.cam}/${routes.activityDashboard}/${routes.payment}/${routes.reports}`,
              name: <span>Payment 360 Report</span>,
            },
            {
              link: "",
              name: <span className="text-primary">Invoice List</span>,
            },
          ],
    []
  );

  const onBackClick = () =>
    navigate(
      `/${routes.cam}/${
        fromDashboard ? routes.dashboard : routes.activityDashboard
      }/${routes.payment}/${routes.reports}`
    );

  const breadcrumbData = isFromPayment360
    ? breadcrumbDataPayment360
    : breadcrumbDataPayment;

  const postedColumns = [
    {
      accessorKey: "documentNumber",
      header: (
        <div>
          Posted <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.documentNumber || ""),
      size: 50,
    },
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
      size: 50,
    },
  ];

  const commonColumns = [
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
      size: 50,
    },
  ];

  // Define Table Columns
  const columns = [
    ...(isPosted ? postedColumns : commonColumns),
    {
      accessorKey: "vendorName",
      header: <div>Vendor Name</div>,
      accessorFn: (row: any) => String(row?.vendorName || ""),
    },
    {
      accessorKey: "documentType",
      header: <div>Document Type</div>,
      accessorFn: (row: any) => String(row?.documentType || ""),
    },
    {
      accessorKey: "postingDate",
      header: <div>Posting Date</div>,
      accessorFn: (row: any) =>
        row?.postingDate
          ? moment(row?.postingDate || "").format("MM/DD/YYYY")
          : "",
    },
    {
      accessorKey: "glMasterId",
      header: <div>Account Name</div>,
      accessorFn: (row: any) =>
        glData?.data?.find((data: any) => data?.glMasterId == row?.glMasterId)
          ?.accountShortDesc || "",
    },
    {
      accessorKey: "paymentMethodId",
      header: <div>Payment Method</div>,
      accessorFn: (row: any) =>
        paymentMethodOptions?.data?.find(
          (data: any) =>
            data?.paymentMethodId?.toString() == row?.paymentMethodId?.toString()
        )?.methodName || "",
    },
    {
      accessorKey: "approvedAmount",
      meta: { align: "right" },
      header: <div>Payment Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmount || "0"),
    },
    {
      accessorKey: "paymentDate",
      header: <div>Payment Date</div>,
      accessorFn: (row: any) =>
        row?.paymentDate
          ? moment(row?.paymentDate || "").format("MM/DD/YYYY")
          : "",
    },
    {
      id: "linked_document",
      header: "Linked Document",
      cell: (row: any) => {
        return (
          <Button
            variant="link"
            type="button"
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              const parsed = parseRelatedDocuments(
                row?.row?.original?.relatedDocuments
              );
              dispatch(setInvoiceIds(parsed.linkedInvoiceIds));
              dispatch(setPaymentIds(parsed.linkedPaymentIds));
              setShowLinkPopup(true);
            }}
          >
            View
          </Button>
        );
      },
    },
  ];

  const onRowClick = (row: any) => {
    if (isPosted) {
      navigate(`${row.original.postDocumentHeaderId}/view`);
    } else {
      navigate(`${row.original.parkDocumentHeaderId}/edit`);
    }
  };

  const viewInvoiceDoc = () => {
    if (invoiceIds) {
      dispatch(setInvoiceFrom("payment-listing"));
      navigate(
        `/${routes.cam}/${routes.activityDashboard}/${routes.invoice}/posted`
      );
    }
  };

  const viewPaymentDoc = () => {
    isViewPaymentDoc.current = true;
    setPageNumber(0);
    setShowLinkPopup(false);
    setTimeout(() => refetchDoc(), 100);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    paymentAction,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    requiredFields,
    onSubmit,
    onClear,
    isPaymentMethodLoading,
    isLoadingGlMaster,
    showLinkPopup,
    setShowLinkPopup,
    invoiceIds,
    viewInvoiceDoc,
    isFromPayment360,
    onBackClick,
    paymentIds,
    viewPaymentDoc
  };
};

export default usePaymentListing;
